import type { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';

import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Translation } from '@/hoslog-intl';
import { useDynamicLocale } from '@/hoslog-intl/Translation';
import { EditIcon, KeySolidIcon, LockCloseIcon, LockOpenIcon, TrashIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { ADMIN_STAFF_STATUS, AdminStaff } from '@/services/adminStaff/adminStaff.types';
import { AddStaffForm } from '../AddStaffForm';
import { Can } from '@/components/can/Can';
import { PERMS } from '@/features/auth/permissions/permissionKeys';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

interface GetColumnsProps {
  openConfirmation: OpenConfirmationProps;
  mutateAsync: (staffId: string) => Promise<unknown>;
  t: ReturnType<typeof useDynamicLocale>['t'];
  onOpen: <T extends string>(config: {
    title: T;
    modalTitle: string | null;
    okText: React.ReactNode;
    component: React.ReactNode;
    cancelText?: string | React.ReactNode;
    size?: ModalSize;
    onCancel?: () => void;
  }) => void;
  resetPassword: (staffId: string) => Promise<unknown>;
  updateStatus: (payload: { staffId: string; status: string }) => Promise<unknown>;
}

export function getColumns({
  openConfirmation,
  mutateAsync,
  t,
  onOpen,
  resetPassword,
  updateStatus,
}: GetColumnsProps): ColumnDef<AdminStaff>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title="S.N" />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary truncate max-w-[10rem]">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'username',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('userManagement.adminStaffs.table.userName')}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption text-primary truncate max-w-[20rem]">{row.getValue('username')}</div>
      ),
      meta: {
        label: 'Username',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'emailAddress',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.adminStaffs.table.emailAddress')} />
      ),
      cell: ({ row }) => {
        const email: string = row.getValue('emailAddress');
        return (
          <a href={`mailto:${email}`} className="text-link font-medium text-caption underline">
            {email}
          </a>
        );
      },
      meta: {
        label: 'Email Address',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'phoneNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.adminStaffs.table.phoneNumber')} />
      ),
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('phoneNumber')}</div>,
      meta: {
        label: 'Phone Number',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'role',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.adminStaffs.table.role')} />
      ),
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('role')}</div>,
      meta: {
        label: 'Role',
      },
      enableHiding: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          className="whitespace-nowrap"
          title={t('userManagement.adminStaffs.table.status')}
        />
      ),
      cell: ({ cell }) => {
        const status = cell.getValue<AdminStaff['status']>();
        return (
          <div>
            {status === ADMIN_STAFF_STATUS.ACTIVE ? (
              <div className="text-success font-medium text-caption">
                <Translation id={'common.page.table.active'} />
              </div>
            ) : status === ADMIN_STAFF_STATUS.BLOCKED ? (
              <div className="text-destructive-text font-medium text-caption">
                <Translation id={'common.page.table.blocked'} />
              </div>
            ) : (
              <div>-</div>
            )}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.adminStaffs.table.createdDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption text-primary whitespace-nowrap">
            {formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD')}
          </div>
        );
      },

      enableHiding: false,
      enableColumnFilter: true,
    },

    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('userManagement.adminStaffs.table.action')}
          className="whitespace-nowrap"
        />
      ),
      cell: ({ row }) => {
        const name = row.getValue('username');
        const isBlocked = row.original.status === ADMIN_STAFF_STATUS.BLOCKED;
        return (
          <div className="flex items-center gap-2  ">
            {isBlocked ? (
              <Can rule={{ allOf: [PERMS.staff.updateStatus] }}>
                <LockOpenIcon
                  size={16}
                  className="cursor-pointer text-link"
                  onClick={() =>
                    openConfirmation({
                      title: t('common.page.modal.confirmation.title'),
                      description: t('common.page.modal.unblockMessage'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.confirm'),
                      onAction: async () => {
                        await updateStatus({ staffId: row.original.staffId, status: ADMIN_STAFF_STATUS.ACTIVE });
                      },
                      onCancel: () => {},
                      size: 'lg',
                      variant: 'warning',
                    })
                  }
                />
              </Can>
            ) : (
              <>
                <Can rule={{ allOf: [PERMS.staff.update] }}>
                  <EditIcon
                    size={16}
                    className="text-foreground-caption hover:text-foreground-detail"
                    onClick={() => {
                      onOpen({
                        title: 'Edit Admin Staff',
                        okText: t('common.page.modal.submit'),
                        size: 'lg',
                        modalTitle: 'common.page.modal.editStaff',
                        component: <AddStaffForm data={row.original} />,
                      });
                    }}
                  />
                </Can>
                <Can rule={{ allOf: [PERMS.staff.delete] }}>
                  <TrashIcon
                    size={16}
                    className="cursor-pointer text-destructive-text hover:text-destructive-text ml-2"
                    onClick={() => {
                      openConfirmation({
                        title: t('common.page.modal.deleteStaff.title'),
                        description: t('userManagement.adminStaffs.deleteModel.message', { staffName: name }),
                        cancelLabel: t('common.page.modal.no'),
                        actionLabel: t('common.page.modal.confirm'),
                        onAction: async () => {
                          await mutateAsync(row.original.staffId);
                        },
                        onCancel: () => {},
                        size: 'lg',
                        variant: 'destructive',
                      });
                    }}
                  />
                </Can>
                <Can rule={{ allOf: [PERMS.staff.resetPassword] }}>
                  <KeySolidIcon
                    size={16}
                    className="cursor-pointer text-warning"
                    onClick={() =>
                      openConfirmation({
                        title: t('common.page.modal.resetPassword.title'),
                        description: t('common.page.modal.resetPasswordMessage'),
                        cancelLabel: t('common.page.modal.no'),
                        actionLabel: t('common.page.modal.confirm'),
                        onAction: async () => {
                          await resetPassword(row.original.staffId);
                        },
                        onCancel: () => {},
                        size: 'lg',
                        variant: 'warning',
                      })
                    }
                  />
                </Can>
                <Can rule={{ allOf: [PERMS.staff.updateStatus] }}>
                  <LockCloseIcon
                    size={16}
                    className="cursor-pointer text-destructive-text"
                    onClick={() =>
                      openConfirmation({
                        title: t('common.page.modal.confirmation.title'),
                        description: t('common.page.modal.blockMessage'),
                        cancelLabel: t('common.page.modal.no'),
                        actionLabel: t('common.page.modal.confirm'),
                        onAction: async () => {
                          await updateStatus({ staffId: row.original.staffId, status: ADMIN_STAFF_STATUS.BLOCKED });
                        },
                        onCancel: () => {},
                        size: 'lg',
                        variant: 'warning',
                      })
                    }
                  />
                </Can>
              </>
            )}
          </div>
        );
      },
      size: 40,
    },
  ];
}
